import { useAuthStore } from '@/store'

/**
 * What this account is allowed to do.
 *
 * The server enforces all of this independently — these flags exist so the UI
 * does not offer buttons that will come back 403. Never treat them as the
 * security boundary: anything gated here must also be gated in PHP.
 *
 * The roles are a ladder, not a set: an admin can do everything a scheduler
 * can, and a scheduler everything a member can.
 */
export function usePermissions() {
  const role = useAuthStore((s) => s.user?.role) ?? 'member'
  const personId = useAuthStore((s) => s.user?.id) ?? null

  const isAdmin = role === 'admin'
  const isScheduler = role === 'admin' || role === 'scheduler'
  /*
   * Sees everything, changes nothing.
   *
   * `viewer` exists for the person who needs to know who is on this Sunday —
   * a pastor, a ministry lead — without being handed every plan, rota and the
   * song library to edit. Read affordances gate on THIS; every edit affordance
   * stays on `canSchedule` or `canEditLibrary`, and the server refuses a
   * viewer's writes on its own because `requireRole` ranks viewer below
   * scheduler.
   */
  const canViewAll = isScheduler || role === 'viewer'

  return {
    role,
    /** This account's row in `people`, for the "is this mine?" checks below. */
    personId,
    isAdmin,
    isScheduler,
    /** See the whole organisation's schedule — NOT permission to change it. */
    canViewAll,
    /**
     * Create, edit and delete songs, arrangements and their media.
     *
     * Admins only, deliberately — this is NOT `canSchedule`. The library is
     * shared and permanent: one deleted song takes its arrangements, chord
     * sheets and reference tracks with it, for every team and every past plan.
     * A scheduler builds Sunday out of the library; they do not get to rewrite
     * it. Everyone else — schedulers included — reads and rehearses.
     */
    canEditLibrary: isAdmin,
    /** Build plans, edit the running order, and schedule people. */
    canSchedule: isScheduler,
    /** Ministries, teams, service types, accounts, keys and settings. */
    canAdminister: isAdmin,
    /**
     * Accept or decline. A volunteer answers their OWN request and nothing
     * else — the same rule `PATCH /assignments/:id` enforces, where a member
     * may send `status` and `declineReason` on their own row and is refused
     * anything more. Keep the two in step.
     */
    canAnswerFor: (assignedPersonId: string | null | undefined) =>
      isScheduler || (personId !== null && assignedPersonId === personId),
  }
}
